import { DocumentDuplicateIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { omit, orderBy } from 'lodash'
import outdent from 'outdent'
import { MouseEvent, useEffect, useState } from 'react'
import { Link, NavLink, Redirect, Route, Switch, useHistory } from 'react-router-dom'

import proxyAction from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/globalActions/proxy.json'
import useDatasource from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useDatasource'
import { renderHelpText, requiredActionKeys } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/utilities/constants'
import cn from '@/utilities/cn'

import { Inputs } from './Inputs'
import { Legacy } from './Legacy'
import { Metadata } from './Metadata'
import { Perform } from './Perform'
import { Response } from './Response'

export function Actions() {
  const [datasource, setDatasource] = useDatasource(true)

  const [activeAction, setActiveAction] = useState<string>(() => {
    const item = localStorage.getItem(`${datasource.id}-active-action`)
    return item && Object.keys(datasource.actions).includes(item) ? item : '_auth'
  })

  const [activeTab, setActiveTab] = useState<string>(
    () => localStorage.getItem(`${datasource.id}-active-action-tab`) || 'metadata'
  )

  const datasourceActions: typeof datasource.actions = { _proxy: proxyAction, ...datasource.actions }

  const history = useHistory()

  const orderedActions = orderBy(Object.keys(datasourceActions), value => value.toLowerCase())

  useEffect(() => {
    return history.listen((location, action) => {
      if (action !== 'PUSH') return

      const selectedTab = location.pathname.split('/')[2]
      if (selectedTab) {
        localStorage.setItem(`${datasource.id}-active-action-tab`, selectedTab)
        setActiveTab(selectedTab)
      }

      const selectedAction = location.search.split('?action=')[1]
      if (selectedAction) {
        localStorage.setItem(`${datasource.id}-active-action`, selectedAction)
        setActiveAction(selectedAction)
      }
    })
  }, [history, datasource.id])

  const handleAdd = () => {
    const action = '__resource__/__new-action__'

    setDatasource(datasource => ({
      ...datasource,
      actions: {
        ...datasource.actions,
        [action]: {
          inputs: { type: 'object', properties: {}, required: [] },
          perform: outdent`
            ${renderHelpText()}
            const response = await client.request({ url: inputs.baseUrl, method: 'GET', responseType: 'json' })
            export default response
          `
        }
      }
    }))

    history.push(`/actions/metadata?action=${action}`)
  }

  const handleDuplicate = (event: MouseEvent, actionKey: string) => {
    event.preventDefault()

    const action = `${actionKey}_copy`

    setDatasource(datasource => ({
      ...datasource,
      actions: {
        ...datasource.actions,
        [action]: datasourceActions[actionKey]
      }
    }))

    history.push(`/actions/metadata?action=${action}`)
  }

  const handleRemove = (event: MouseEvent, actionKey: string) => {
    event.preventDefault()

    setDatasource(datasource => ({
      ...datasource,
      actions: omit(datasource.actions, [actionKey])
    }))

    const deletedIndex = orderedActions.indexOf(actionKey)
    const newIndex = deletedIndex === orderedActions.length - 1 ? deletedIndex - 1 : deletedIndex

    history.push(`/actions?action=${orderedActions.filter(a => a !== actionKey)[newIndex]}`)
  }

  const handleMetadataChange = (key: string) => {
    localStorage.setItem(`${datasource.id}-active-action`, key)
    setActiveAction(key)
  }

  return (
    <div className="flex flex-1 divide-x">
      <div className="flex w-96 flex-shrink-0 p-4">
        <nav className="flex h-[calc(100vh-270px)] w-full flex-1 flex-col">
          <div className="overflow-auto">
            <ul className="-mx-2 flex-1 space-y-1">
              {orderedActions.map(actionKey => (
                <li key={actionKey}>
                  <Link
                    to={`/actions?action=${actionKey}`}
                    className={cn(
                      'flex justify-between gap-x-3 rounded-md py-2 px-3',
                      activeAction === actionKey ? 'bg-gray-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold leading-6">
                      {datasourceActions[actionKey].name || actionKey}
                    </span>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        title="Duplicate"
                        className="flex items-center"
                        onClick={event => handleDuplicate(event, actionKey)}
                      >
                        <DocumentDuplicateIcon
                          className="h-5 w-5 text-gray-400 hover:text-gray-500"
                          aria-hidden="true"
                        />
                      </button>

                      <button
                        type="button"
                        title={requiredActionKeys.has(actionKey) ? 'Required actions cannot be removed' : 'Remove'}
                        className={cn(
                          'flex items-center rounded-md bg-red-600 px-1 shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600',
                          { 'bg-red-300 hover:bg-red-300': requiredActionKeys.has(actionKey) }
                        )}
                        onClick={event => handleRemove(event, actionKey)}
                        disabled={requiredActionKeys.has(actionKey)}
                      >
                        <XMarkIcon className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            className="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            onClick={handleAdd}
          >
            Add Action
          </button>
        </nav>
      </div>

      <div className="flex flex-1 flex-col">
        {activeAction ? (
          <>
            <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:px-6 lg:px-8">
              <nav>
                <ul className="flex gap-x-6 text-sm font-semibold leading-6">
                  <li>
                    <NavLink
                      to={`/actions/metadata?action=${activeAction}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Metadata
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={`/actions/inputs?action=${activeAction}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Inputs
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={`/actions/perform?action=${activeAction}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Perform
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={`/actions/response?action=${activeAction}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Response
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={`/actions/legacy?action=${activeAction}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Legacy
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={`/test?action=${activeAction}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Test
                    </NavLink>
                  </li>
                </ul>
              </nav>
            </div>
            <div className="flex flex-1">
              <Switch>
                <Route path="/actions/metadata" render={() => <Metadata onChange={handleMetadataChange} />} />
                <Route path="/actions/inputs" render={() => <Inputs />} />
                <Route path="/actions/response" render={() => <Response />} />
                <Route path="/actions/legacy" render={() => <Legacy />} />
                <Route path="/actions/perform" render={() => <Perform />} />
                <Redirect to={`/actions/${activeTab}?action=${activeAction}`} />
              </Switch>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}
